import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { UserPlus, MoreHorizontal, Search, Users } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { RoleChip } from '@/components/shared/role-chip'
import { EmptyState } from '@/components/shared/empty-state'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { fetchMembers, removeMember, type Member } from '../api/members-api'
import { InviteMemberModal } from '../components/invite-member-modal'
import { ChangeRoleModal } from '../components/change-role-modal'
import { MemberDetailPanel } from '../components/member-detail-panel'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import dayjs from 'dayjs'

export function MembersPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)

  const [search, setSearch] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [changingRole, setChangingRole] = useState<Member | null>(null)
  const [removing, setRemoving] = useState<Member | null>(null)
  const [viewingMember, setViewingMember] = useState<Member | null>(null)

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['members', projectId],
    queryFn: () => fetchMembers(projectId!),
    enabled: !!projectId,
  })

  const removeMutation = useMutation({
    mutationFn: () => removeMember(removing!.id, user!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', projectId] })
      setRemoving(null)
    },
  })

  const filtered = members.filter(
    (m) =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.email.toLowerCase().includes(search.toLowerCase())
  )

  const adminCount = members.filter((m) => m.role === 'admin').length

  const actions = (
    <Button size="sm" className="gap-1.5" onClick={() => setInviteOpen(true)}>
      <UserPlus className="w-4 h-4" />
      دعوت عضو
    </Button>
  )

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="اعضا"
        breadcrumbs={[{ label: 'اعضا' }]}
        actions={actions}
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="جستجو در اعضا..."
            className="ps-9"
          />
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-4 py-3 text-start font-medium text-muted-foreground text-xs">نام</th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground text-xs">نقش</th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground text-xs">تاریخ عضویت</th>
                <th className="w-10 px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-border">
                      {Array.from({ length: 4 }).map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <div className="h-4 bg-muted rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                : filtered.map((member) => (
                    <tr key={member.id} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => setViewingMember(member)}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                            <span className="text-xs font-medium text-primary">
                              {member.name[0]}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{member.name}</p>
                            {member.isDisabled && (
                              <span className="text-[10px] text-red-400">غیرفعال</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <RoleChip role={member.role} />
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {dayjs(member.joinedAt).format('YYYY/MM/DD')}
                      </td>
                      <td className="px-4 py-3">
                        {member.userId !== user?.id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setChangingRole(member)}>
                                تغییر نقش
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                disabled={member.role === 'admin' && adminCount <= 1}
                                onClick={() => setRemoving(member)}
                              >
                                حذف از پروژه
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>

          {!isLoading && filtered.length === 0 && (
            <EmptyState
              icon={Users}
              title={search ? 'عضوی یافت نشد' : 'هنوز عضوی اضافه نشده'}
              action={
                !search ? (
                  <Button size="sm" onClick={() => setInviteOpen(true)}>
                    <UserPlus className="w-4 h-4 me-1.5" />
                    دعوت عضو
                  </Button>
                ) : undefined
              }
            />
          )}
        </div>
      </div>

      {projectId && (
        <>
          <InviteMemberModal
            projectId={projectId}
            open={inviteOpen}
            onClose={() => setInviteOpen(false)}
          />
          <ChangeRoleModal
            projectId={projectId}
            member={changingRole}
            open={!!changingRole}
            onClose={() => setChangingRole(null)}
          />

          {/* Remove confirmation */}
          <Dialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>حذف عضو از پروژه؟</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                آیا مطمئن هستید که می‌خواهید <span className="font-medium text-foreground">{removing?.name}</span> را از این پروژه حذف کنید؟
                تاریخچه فعالیت‌های او حفظ می‌شود.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRemoving(null)}>انصراف</Button>
                <Button
                  variant="destructive"
                  onClick={() => removeMutation.mutate()}
                  disabled={removeMutation.isPending}
                >
                  {removeMutation.isPending ? 'در حال حذف...' : 'حذف از پروژه'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Member detail panel */}
      {viewingMember && projectId && (
        <MemberDetailPanel
          member={viewingMember}
          projectId={projectId}
          onClose={() => setViewingMember(null)}
          onChangeRole={() => { setChangingRole(viewingMember); setViewingMember(null) }}
          onRemove={() => { setRemoving(viewingMember); setViewingMember(null) }}
        />
      )}
    </div>
  )
}
